import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { DotLottie } from '@lottiefiles/dotlottie-web'
import wasmUrl from '@lottiefiles/dotlottie-web/dotlottie-player.wasm?url'

gsap.registerPlugin(ScrollTrigger)
DotLottie.setWasmUrl(wasmUrl)

const STEP_DURATION = 6;

let mm;

const createLotties = (steps, mobile) => steps.map((step) => {
	const canvas = step.querySelector('[data-process-lottie]')

	if (!canvas) return null

	const lottie = new DotLottie({
		canvas,
		src: (mobile && canvas.dataset.processLottieMobile) || canvas.dataset.processLottie,
		loop: true,
		renderConfig: { autoResize: true }
	})

	// Size the canvas to the artboard; play() is ignored until the file has loaded
	lottie.addEventListener('load', () => {
		const { width, height } = lottie.animationSize()

		canvas.style.setProperty('--width', width)
		canvas.style.setProperty('--height', height)

		if (step.classList.contains('is-active')) lottie.play()
	})

	return lottie
})

export const initProcess = () => {
	mm?.revert()

	const sections = document.querySelectorAll('[data-process]')

	if (sections.length === 0) return

	mm = gsap.matchMedia()

	sections.forEach((section) => {
		const steps = [...section.querySelectorAll('[data-process-step]')]
		const triggers = steps.map((step) => step.querySelector('[data-process-trigger]'))

		if (steps.length === 0) return

		let players = []

		const setActive = (index) => {
			steps.forEach((step, i) => {
				const isActive = i === index
				const video = step.querySelector('video')
				const lottie = players[i]

				step.classList.toggle('is-active', isActive)
				triggers[i].setAttribute('aria-expanded', isActive)

				if (isActive) {
					video?.play().catch(() => {})
					lottie?.play()
				} else {
					video?.pause()
					lottie?.stop()
				}
			})
		}

		mm.add('(min-width: 651px)', (context) => {
			const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
			const lotties = createLotties(steps, false)
			let active = 0
			let inView = false
			let hovered = false
			let focused = false
			let timer

			players = lotties

			// Only run the loop while the section is on screen and not being interacted with
			const sync = () => {
				if (!timer) return

				if (inView && !hovered && !focused) {
					timer.resume()
				} else {
					timer.pause()
				}
			}

			const schedule = context.add('schedule', () => {
				timer?.kill()

				if (reduceMotion) return

				timer = gsap.delayedCall(STEP_DURATION, () => goTo((active + 1) % steps.length))
				sync()
			})

			const goTo = (index) => {
				active = index
				setActive(active)
				schedule()
			}

			goTo(0)

			ScrollTrigger.create({
				trigger: section,
				start: 'top 75%',
				end: 'bottom 25%',
				onToggle: (self) => {
					inView = self.isActive
					sync()
				}
			})

			const onClick = triggers.map((trigger, i) => () => goTo(i))
			const onMouseEnter = () => { hovered = true; sync() }
			const onMouseLeave = () => { hovered = false; sync() }
			const onFocusIn = (e) => { focused = e.target.matches(':focus-visible'); sync() }
			const onFocusOut = () => { focused = false; sync() }

			triggers.forEach((trigger, i) => trigger.addEventListener('click', onClick[i]))
			section.addEventListener('mouseenter', onMouseEnter)
			section.addEventListener('mouseleave', onMouseLeave)
			section.addEventListener('focusin', onFocusIn)
			section.addEventListener('focusout', onFocusOut)

			return () => {
				timer?.kill()
				lotties.forEach((lottie) => lottie?.destroy())
				triggers.forEach((trigger, i) => trigger.removeEventListener('click', onClick[i]))
				section.removeEventListener('mouseenter', onMouseEnter)
				section.removeEventListener('mouseleave', onMouseLeave)
				section.removeEventListener('focusin', onFocusIn)
				section.removeEventListener('focusout', onFocusOut)
			}
		})

		mm.add('(max-width: 650px)', () => {
			const lotties = createLotties(steps, true)
			let active = 0

			players = lotties
			setActive(active)

			const onClick = triggers.map((trigger, i) => () => {
				active = active === i ? -1 : i
				setActive(active)
			})

			triggers.forEach((trigger, i) => trigger.addEventListener('click', onClick[i]))

			return () => {
				lotties.forEach((lottie) => lottie?.destroy())
				triggers.forEach((trigger, i) => trigger.removeEventListener('click', onClick[i]))
			}
		})
	})
}
